import { createContext, useContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_RESOURCE_LIBRARY_NEW_PRODUCT_INTRODUCTION_ITEMS_QUERY,
    getResourceLibraryNewProductIntroductionItemsQueryVariables,
} from '../queries/getResourceLibraryNewProductIntroductionItemsQuery';
import { GetResourceLibraryNewProductIntroductionItemListingQuery } from '../schemas/generated-types/graphql';
import { ResourceLibraryNewProductIntroductionBaseItem } from '../types/resourceLibraryItems';
import { transformResourceLibraryNewProductIntroductionItemsResponse } from '../tools/transformResourceLibraryNewProductIntroductionItemsResponse';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface ResourceLibraryNewProductIntroductionItemContextValue {
    items: ResourceLibraryNewProductIntroductionBaseItem[];
    loading: boolean;
    error: any;
    refetch: () => void;
    initialize: () => void;
}

const ResourceLibraryNewProductIntroductionItemContext = createContext<
    ResourceLibraryNewProductIntroductionItemContextValue | undefined
>(undefined);

interface ProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ResourceLibraryNewProductIntroductionItemsProvider = ({
    children,
    apolloClient,
}: ProviderProps) => {
    const { locale } = useLocale();
    const [initialized, setInitialized] = useState(false);

    const query = GET_RESOURCE_LIBRARY_NEW_PRODUCT_INTRODUCTION_ITEMS_QUERY;

    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetResourceLibraryNewProductIntroductionItemListingQuery>(query, {
        variables: getResourceLibraryNewProductIntroductionItemsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all',
        skip: !initialized,
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError });

    const refetch = () => {
        if (initialized) {
            apolloClient.refetchQueries({ include: 'active' });
        } else {
            initialize();
        }
    };

    const initialize = () => {
        if (!initialized) setInitialized(true);
    };

    const items = useMemo(() => {
        const source = fallbackData ?? queryData;
        if (!source) return [];
        return transformResourceLibraryNewProductIntroductionItemsResponse(source);
    }, [queryData, fallbackData]);

    const loading = loadingQuery || loadingFallback;
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ResourceLibraryNewProductIntroductionItemContext.Provider
            value={{ items, loading, error, refetch, initialize }}
        >
            {children}
        </ResourceLibraryNewProductIntroductionItemContext.Provider>
    );
};

function useResourceLibraryNewProductIntroductionItemsUnsafe() {
    const context = useContext(ResourceLibraryNewProductIntroductionItemContext);
    if (!context) {
        throw new Error(
            'useResourceLibraryNewProductIntroductionItems must be used within its Provider'
        );
    }
    return context;
}

function useResourceLibraryNewProductIntroductionItems() {
    const context = useResourceLibraryNewProductIntroductionItemsUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export {
    ResourceLibraryNewProductIntroductionItemsProvider,
    useResourceLibraryNewProductIntroductionItems,
    useResourceLibraryNewProductIntroductionItemsUnsafe,
};
export type { ResourceLibraryNewProductIntroductionItemContextValue };
